/**
 * react-query wiring for `/api/teacher/prep*` (lecture-prep workbench). The server
 * summary type is mirrored by hand, as in `../types.ts`; the lecture body type comes
 * from the schema (type-only import, so no prep content reaches the client bundle).
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@client/api/client";
import type { PrepKind, PrepLecture } from "@content/prepSchema";

export type PrepReviewStatus = "draft" | "reviewed";

/** Mirrors `PrepSummary` in `server/src/features/teacher/prep.ts`. */
export interface PrepSummary {
  id: string;
  kind: PrepKind | null;
  title: string;
  /** Calendar day `YYYY-MM-DD`, or null when the lecture is unscheduled. */
  date: string | null;
  objectOfLearning: string | null;
  available: boolean;
  reviewStatus: PrepReviewStatus | null;
}

export interface PrepLectureResponse {
  lecture: PrepLecture;
  date: string | null;
}

export const prepKeys = {
  list: () => ["teacher", "prep"] as const,
  lecture: (id: string) => ["teacher", "prep", id] as const,
};

export function usePrepList() {
  return useQuery({
    queryKey: prepKeys.list(),
    queryFn: () => api.get<{ lectures: PrepSummary[] }>("/api/teacher/prep"),
  });
}

export function usePrepLecture(id: string) {
  return useQuery({
    queryKey: prepKeys.lecture(id),
    queryFn: () => api.get<PrepLectureResponse>(`/api/teacher/prep/${encodeURIComponent(id)}`),
    enabled: id.length > 0,
  });
}
